<script setup lang="ts">
import { ActionButton, ActionIcon, Mark, NavIcon } from '@taler/ui'
import { computed, onMounted, provide, ref, useTemplateRef, watch, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { canCreateEntries, FAVOURITE_ACCOUNT_KEY, useFavouriteAccountId } from '@/features/entries'
import { useLedgerWorkingCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'
import AccountMenu from '@/app/shell/AccountMenu.vue'
import OfflineNotice from '@/app/shell/OfflineNotice.vue'
import LedgerSwitch from '@/app/shell/LedgerSwitch.vue'
import LocaleSwitch from '@/app/shell/LocaleSwitch.vue'
import DisplayCurrencySwitch from '@/app/shell/DisplayCurrencySwitch.vue'
import { useSidebar } from './sidebar.ts'
import { NotificationBell } from '@/features/notifications'
import { type NavGroup, type NavItem, visibleNavItems } from './nav-items.ts'

const { t } = useI18n()
const currentRoute = useRoute()
const session = useSession()
provide(
  FAVOURITE_ACCOUNT_KEY,
  useFavouriteAccountId(() => session.activeLedger.value?.id),
)
useLedgerWorkingCurrency()
const sidebar = useSidebar()
const navItems = computed(() => visibleNavItems(session.can))
const canAddEntries = computed(() => canCreateEntries(session.can))
// Hidden on the entry page itself, where Post is the one action.
const onNewEntries = computed(() => currentRoute.name === 'entries-new')
const newEntries = useTemplateRef<ComponentPublicInstance>('newEntries')
watch(onNewEntries, (hidden) => {
  if (hidden && document.activeElement === newEntries.value?.$el)
    document.getElementById('shell-main')?.focus()
})

// One block per group, in first-appearance order; a group every item of which is hidden for
// this role disappears with them rather than leaving an orphaned heading.
const navGroups = computed(() => {
  const groups: { key: NavGroup; items: NavItem[] }[] = []
  for (const item of navItems.value) {
    const last = groups.at(-1)
    if (last?.key === item.group) last.items.push(item)
    else groups.push({ key: item.group, items: [item] })
  }
  return groups
})

const GROUP_LABEL_KEYS: Readonly<Record<NavGroup, string | undefined>> = {
  places: undefined,
  insight: 'shell.navGroupInsight',
  shared: 'shell.navGroupShared',
}

// The strike plays once per visit, at the first authenticated paint - not on every
// route change back through the shell.
const STRUCK_KEY = 'taler.mark-struck'
const struck = ref(sessionStorage.getItem(STRUCK_KEY) !== null)
onMounted(() => {
  if (!struck.value) sessionStorage.setItem(STRUCK_KEY, '1')
})
</script>

<template>
  <div class="flex min-h-screen" :data-sidebar="sidebar.collapsed.value ? 'rail' : 'open'">
    <a
      href="#shell-main"
      class="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-20 focus:rounded-[var(--radius-control)] focus:bg-[color:var(--color-surface)] focus:px-3 focus:py-2 focus:text-[color:var(--color-text)]"
    >
      {{ t('shell.skipToContent') }}
    </a>

    <!-- The sidebar is the page's banner: brand, the active ledger, navigation and the account
         controls. A 232px column from 1280px up; below that - down to a 1024px tablet, the
         narrowest target - it collapses to a 64px icon rail, every label kept as the link's
         accessible name. There is no phone layout: narrower viewports scroll. -->
    <header
      id="shell-sidebar"
      class="sticky top-0 z-30 flex h-screen w-16 shrink-0 flex-col gap-[var(--space-group)] overflow-x-hidden overflow-y-auto border-e border-[color:var(--color-border)] px-[var(--space-inline)] py-[var(--space-group)] transition-[width,padding] duration-[var(--motion-base)] ease-[var(--ease-out)] wide:w-58 wide:px-[var(--space-cell)] motion-reduce:transition-none"
    >
      <div class="flex items-center justify-between">
        <span
          class="flex items-center justify-center gap-[var(--space-inline)] font-[family-name:var(--font-display)] text-[length:var(--text-step-5)] font-semibold tracking-[var(--tracking-tight)] text-[color:var(--color-text)] wide:justify-start wide:px-[var(--space-inline)]"
        >
          <Mark :size="24" :strike="!struck" />
          <span class="hidden wide:inline">{{ t('app.name') }}</span>
        </span>
      </div>
      <div class="contents">
        <!-- The active ledger is the one thing that must never be mistaken: it sits directly
           under the mark, above every destination, at a heavier weight than any other control. -->
        <LedgerSwitch />
        <DisplayCurrencySwitch />

        <!-- The shell's one primary action; a button, not a place in the nav. -->
        <ActionButton
          ref="newEntries"
          v-if="canAddEntries"
          v-show="!onNewEntries"
          :as="RouterLink"
          :to="{ name: 'entries-new' }"
          :title="t('entries.title')"
          class="w-full justify-center wide:justify-start"
        >
          <ActionIcon name="plus" />
          <span class="sr-only whitespace-nowrap wide:not-sr-only">{{ t('entries.title') }}</span>
        </ActionButton>

        <nav
          v-if="navItems.length > 0"
          id="shell-primary-nav"
          :aria-label="t('shell.primaryNav')"
          class="flex flex-col gap-[var(--space-group)]"
        >
          <div
            v-for="group in navGroups"
            :key="group.key"
            role="group"
            :aria-label="GROUP_LABEL_KEYS[group.key] ? t(GROUP_LABEL_KEYS[group.key]!) : undefined"
            class="flex flex-col gap-[var(--space-hair)]"
          >
            <p
              v-if="GROUP_LABEL_KEYS[group.key]"
              aria-hidden="true"
              class="hidden px-[var(--space-inline)] pb-[var(--space-tight)] text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)] wide:block"
            >
              {{ t(GROUP_LABEL_KEYS[group.key]!) }}
            </p>
            <span
              v-if="GROUP_LABEL_KEYS[group.key]"
              aria-hidden="true"
              class="mx-auto block h-px w-6 bg-[color:var(--color-border)] wide:hidden"
            />
            <RouterLink
              v-for="item in group.items"
              :key="item.key"
              :to="item.to"
              :title="t(item.labelKey)"
              class="relative flex min-h-[var(--density-control)] items-center justify-center gap-[var(--space-cell)] rounded-[var(--radius-control)] px-[var(--space-inline)] text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text-muted)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-sunken)] hover:text-[color:var(--color-text)] wide:justify-start"
            >
              <NavIcon :name="item.icon" />
              <span class="sr-only whitespace-nowrap wide:not-sr-only">{{ t(item.labelKey) }}</span>
            </RouterLink>
          </div>
        </nav>
        <p
          v-else
          id="shell-primary-nav"
          class="sr-only text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] wide:not-sr-only"
        >
          {{ t('shell.noNavigation') }}
        </p>

        <button
          type="button"
          class="mt-auto hidden min-h-[var(--density-control)] items-center justify-center gap-[var(--space-cell)] rounded-[var(--radius-control)] px-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface-sunken)] hover:text-[color:var(--color-text)] xl:flex"
          :aria-expanded="!sidebar.collapsed.value"
          aria-controls="shell-sidebar"
          :title="sidebar.collapsed.value ? t('shell.expandSidebar') : t('shell.collapseSidebar')"
          @click="sidebar.toggle()"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            class="shrink-0"
            :class="sidebar.collapsed.value ? 'rotate-180' : ''"
          >
            <path d="M9.5 3.5 5 8l4.5 4.5" />
            <path d="M12.5 3v10" />
          </svg>
          <span class="wide:inline hidden">{{ t('shell.collapseSidebar') }}</span>
          <span class="sr-only wide:hidden">{{ t('shell.expandSidebar') }}</span>
        </button>

        <div
          class="mt-auto flex flex-col items-center gap-[var(--space-inline)] border-t xl:mt-0 border-[color:var(--color-border)] pt-[var(--space-cell)] wide:flex-row wide:justify-between"
        >
          <AccountMenu />
          <NotificationBell />
          <LocaleSwitch />
        </div>
      </div>
    </header>

    <!-- Not a landmark: every page already owns the one <main> on the document. -->
    <div id="shell-main" tabindex="-1" class="relative min-w-0 flex-1">
      <OfflineNotice />
      <RouterView v-slot="{ Component, route }">
        <Transition name="ctx" mode="out-in">
          <component :is="Component" :key="`${route.path}:${session.activeLedger.value?.id}`" />
        </Transition>
      </RouterView>
    </div>
  </div>
</template>
